/**
 * Rules page, D06 (30 Oct 2026): the built-in rule catalogue, with what each
 * rule needs. A rule that waits for an event flag nothing sets today is shown
 * as "Needs data source" with the source in words, and does not run: it is
 * never shown as active.
 */

import { useEffect, useMemo, useState } from 'react';
import { Database } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listBuiltInRules, refusalText, type BuiltInRuleStatus } from '@/lib/detection-response-api';

export function BuiltInRules() {
  const [rules, setRules] = useState<BuiltInRuleStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<'needs' | 'active'>('needs');

  useEffect(() => {
    listBuiltInRules()
      .then((r) => setRules(r.rules))
      .catch((err) => setError(refusalText(err)));
  }, []);

  const shown = useMemo(() => (rules ?? []).filter((r) => (show === 'needs' ? r.status === 'needs_data_source' : r.status === 'active')), [rules, show]);
  const needs = (rules ?? []).filter((r) => r.status === 'needs_data_source').length;

  return (
    <GlassCard className="p-5 mt-4" data-testid="built-in-rules">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2 text-sm"><Database className="h-4 w-4 text-primary" /> Built-in rules</CardTitle>
        <CardDescription>
          {rules === null && !error
            ? 'Loading...'
            : error
              ? `Could not load the built-in rules: ${error}`
              : `${rules!.length - needs} run on evaluation. ${needs} wait for a data source this installation does not have yet, and do not run until it is connected.`}
        </CardDescription>
      </CardHeader>
      <div className="flex gap-2 mb-2">
        <Button size="sm" variant={show === 'needs' ? 'default' : 'glass'} onClick={() => setShow('needs')}>Needs data source ({needs})</Button>
        <Button size="sm" variant={show === 'active' ? 'default' : 'glass'} onClick={() => setShow('active')}>Active ({(rules?.length ?? 0) - needs})</Button>
      </div>
      <ul className="text-xs space-y-1 max-h-72 overflow-y-auto">
        {shown.map((r) => (
          <li key={r.name} className="flex items-start gap-2">
            <Badge variant={r.status === 'active' ? 'success' : 'warning'} className="shrink-0">{r.status === 'active' ? 'Active' : 'Needs data source'}</Badge>
            <div>
              <span className="font-mono">{r.name}</span>
              {r.needsDataSource && <div className="text-muted-foreground">Needs: {r.needsDataSource}</div>}
            </div>
          </li>
        ))}
        {rules !== null && shown.length === 0 && <li className="text-muted-foreground">None.</li>}
      </ul>
    </GlassCard>
  );
}
